import { TextField } from "@mui/material";
import transformDateToDe from "../../../functions/transformDateToDe";

interface EndOfQualificationProps {
  disabled?: boolean;
  required: boolean;
  endOfQualification: string;
  startOfQualification: string;
  minDate?: string;
  showValidation?: boolean;
  onBlur?: () => void;
  setEndOfQualification: (endOfQualification: string) => void;
}

const isBeforeMinDate = (
  endOfQualification: string,
  minDate: string | undefined,
): minDate is string => {
  return (
    minDate !== undefined &&
    endOfQualification.length > 0 &&
    endOfQualification < minDate
  );
};

const isInvalid = (
  endOfQualification: string,
  startOfQualification: string,
  minDate: string | undefined,
): boolean => {
  return (
    endOfQualification.length === 0 ||
    startOfQualification > endOfQualification ||
    startOfQualification.length === 0 ||
    isBeforeMinDate(endOfQualification, minDate)
  );
};

const renderHelperText = (
  endOfQualification: string,
  startOfQualification: string,
  minDate: string | undefined,
): string => {
  if (endOfQualification.length === 0) {
    return "Bitte gib ein Datum ein";
  } else if (isBeforeMinDate(endOfQualification, minDate)) {
    return `Das Datum darf nicht vor dem ${transformDateToDe(minDate)} liegen`;
  } else if (startOfQualification > endOfQualification) {
    return "Das Enddatum muss nach dem Startdatum liegen";
  } else if (startOfQualification.length === 0) {
    return "Bitte gib ein Startdatum ein";
  } else {
    return "";
  }
};

const EndOfQualification = ({
  disabled,
  required,
  endOfQualification,
  startOfQualification,
  minDate,
  setEndOfQualification,
  showValidation = true,
  onBlur,
}: EndOfQualificationProps): React.JSX.Element => {
  const hasInvalidValue = isInvalid(
    endOfQualification,
    startOfQualification,
    minDate,
  );
  const showError = showValidation && hasInvalidValue;

  return (
    <TextField
      sx={{
        "& .MuiOutlinedInput-notchedOutline": {
          borderWidth: hasInvalidValue ? "1px !important " : "2px !important",
        },
        "& .MuiInputBase-root": {
          color: showError
            ? (theme) => theme.palette.error.main
            : !hasInvalidValue
              ? (theme) => theme.palette.success.main
              : endOfQualification.length === 0
                ? // Match the label colour of the other empty fields for "tt.mm.jjjj".
                  (theme) => theme.palette.text.secondary
                : undefined,
        },
      }}
      disabled={disabled}
      color="success"
      required={required}
      fullWidth
      type="date"
      focused={!hasInvalidValue}
      InputLabelProps={{ shrink: true }}
      value={endOfQualification}
      inputProps={{ min: minDate }}
      id="EndOfQualification"
      label="Ende der Qualifizierung"
      variant="outlined"
      onChange={(event) => setEndOfQualification(event.target.value)}
      onBlur={onBlur}
      error={showError}
      helperText={
        showError
          ? renderHelperText(endOfQualification, startOfQualification, minDate)
          : ""
      }
    />
  );
};

export default EndOfQualification;
